import { FormikField } from 'components/common/form/field/FormikField';
import { Modal } from 'components/common/Modal/Modal';
import {
  CampaignAdTypeSettingFormData,
  CampaignAdTypeState,
  ChannelGroupFormData,
  ChannelType,
  ScreenDisplayType
} from 'core/channelGroup/ChannelGroup';
import { OutdoorType } from 'core/creative/Creative';
import { Formik, useFormikContext } from 'formik';
import i18n from 'i18n';
import { useCallback, useMemo } from 'react';
import { createSelectOptions } from 'utils/SelectOptionsUtils';
import styles from './channelGroupAdTypeForm.module.scss';
import {
  validateEmpty,
  validateMinimum,
  validateMinMax
} from 'utils/ValidateUtils';
import { camelCase, isEmpty, isUndefined, omitBy } from 'lodash';
import { RtbCampaignPlanType } from 'core/rtbCampaign/RtbCampaign';
import { SwitchField } from 'components/common/form/field/SwitchField';

const MIN_DURATION_SECONDS = 1;
/** 電子紙渠道的素材最短秒數下限（ECOPD-5673）。 */
const EPAPER_MIN_DURATION_SECONDS = 300;
const MAX_DURATION_SECONDS = 3600;

export const ChannelGroupAdTypeForm: React.FC<{
  adTypeId?: number;
  allAdTypes: CampaignAdTypeSettingFormData[];
  onClose: () => void;
}> = ({ adTypeId, allAdTypes, onClose }) => {
  const { values: channelGroupValues, setValues } =
    useFormikContext<ChannelGroupFormData>();
  const isStoreOutdoor = channelGroupValues.channelType !== ChannelType.BEACON;
  const isEpaperChannel =
    channelGroupValues.channelType === ChannelType.E_PAPER;

  const {
    isScreenSpliced,
    isMultiScreenSameVideo,
    screenPixelWidth,
    screenPixelHeight,
    screenSplicedHorizontalScreenCount,
    screenSplicedVerticalScreenCount
  } = channelGroupValues.screenProperty || {
    isScreenSpliced: false,
    isMultiScreenSameVideo: false,
    screenPixelWidth: 1920,
    screenPixelHeight: 1080,
    screenSplicedHorizontalScreenCount: 1,
    screenSplicedVerticalScreenCount: 1
  };

  const otherAdTypes = useMemo(() => {
    if (adTypeId === undefined) {
      return allAdTypes;
    }
    return allAdTypes.filter((adType) => adType.adTypeId !== adTypeId);
  }, [adTypeId, allAdTypes]);

  const pricingModelOptions = useMemo(() => {
    if (channelGroupValues.channelType === ChannelType.BEACON) {
      return [
        {
          label: i18n.t('campaign.labels.fbcpm'),
          value: RtbCampaignPlanType.FBCPM
        }
      ];
    }
    const options = [
      {
        label: i18n.t('campaign.labels.fcpm'),
        value: RtbCampaignPlanType.FCPM
      },
      {
        label: i18n.t('campaign.labels.rs'),
        value: RtbCampaignPlanType.RS
      },
      {
        label: i18n.t('campaign.labels.fwcpm'),
        value: RtbCampaignPlanType.FWCPM
      },
      {
        label: i18n.t('campaign.labels.facpm'),
        value: RtbCampaignPlanType.FACPM
      }
    ];
    // 只留 固定式 CPM 與 動態競價：MOVABLE_OUTDOOR 依 RKB-2541、E_PAPER 依
    // ECOPD-5617 的 Final Spec，兩者的結論剛好一樣（移除固定式 wCPM & aCPM）。
    const weatherAndAirQualityExcluded = [
      ChannelType.MOVABLE_OUTDOOR,
      ChannelType.E_PAPER
    ].includes(channelGroupValues.channelType);
    if (weatherAndAirQualityExcluded) {
      const excluded = new Set<RtbCampaignPlanType>([
        RtbCampaignPlanType.FWCPM,
        RtbCampaignPlanType.FACPM
      ]);
      return options.filter((option) => !excluded.has(option.value));
    }
    return options;
  }, [channelGroupValues.channelType]);

  const defaultPriceModels = useMemo(() => {
    return pricingModelOptions.map((option) => option.value);
  }, [pricingModelOptions]);

  const initChannelGroupAdTypeSetting: CampaignAdTypeSettingFormData =
    useMemo(() => {
      if (adTypeId) {
        const adTypeSetting = channelGroupValues.campaignAdTypeSettings.find(
          (setting) => setting.adTypeId === adTypeId
        );
        if (adTypeSetting) {
          return adTypeSetting;
        }
      }

      return {
        name: '',
        nameI18n: {
          ja: '',
          en: '',
          'zh-TW': ''
        },
        state: CampaignAdTypeState.ACTIVE,
        pricingModel: defaultPriceModels,
        screenDisplayType: ScreenDisplayType.SINGLE,
        bannerTypes: isStoreOutdoor
          ? [
              {
                minDuration: isEpaperChannel
                  ? EPAPER_MIN_DURATION_SECONDS
                  : MIN_DURATION_SECONDS,
                // 電子紙一定要有上限：素材表單的預設秒數與分／秒下拉的上界都讀它，
                // 留空會讓素材的預設值退化成最少秒數。其他渠道沿用「不設上限」的既有
                // 資料（廣告活動類型列表對它們有一條專印「N+ 秒」的分支）。
                ...(isEpaperChannel
                  ? { maxDuration: EPAPER_MIN_DURATION_SECONDS }
                  : {}),
                width: screenPixelWidth,
                height: screenPixelHeight,
                supportedOutdoorTypes: []
              }
            ]
          : []
      };
    }, [
      adTypeId,
      channelGroupValues.campaignAdTypeSettings,
      screenPixelWidth,
      screenPixelHeight,
      isStoreOutdoor,
      isEpaperChannel,
      defaultPriceModels
    ]);

  const onSubmit = useCallback(
    (newSetting: CampaignAdTypeSettingFormData) => {
      if (adTypeId) {
        const adTypeSetting = channelGroupValues.campaignAdTypeSettings.find(
          (setting) => setting.adTypeId === adTypeId
        );
        if (adTypeSetting) {
          setValues((prevValues) => {
            return {
              ...prevValues,
              campaignAdTypeSettings: prevValues.campaignAdTypeSettings.map(
                (setting) =>
                  setting.adTypeId === adTypeId
                    ? { ...setting, ...newSetting }
                    : setting
              )
            };
          });
          onClose();
        }
        return;
      }
      setValues((prevValues) => {
        const newTmpId = prevValues.campaignAdTypeSettings.length + 1;
        const newBidPriceSetting =
          prevValues.channelType === ChannelType.BEACON
            ? {
                tmpId: newTmpId,
                autoBidCap: {
                  bcpm: 40
                },
                bidFloor: {
                  bcpm: 1.5
                },
                type: newSetting.name
              }
            : {
                tmpId: newTmpId,
                autoBidCap: {
                  cpc: 0.25,
                  cpm: 250,
                  cpv: 0.25,
                  vCpm: 250
                },
                bidFloor: {
                  cpc: 0.25,
                  cpm: 250,
                  cpv: 0.25,
                  vCpm: 250
                },
                type: newSetting.name
              };
        return {
          ...prevValues,
          campaignAdTypeSettings: [
            ...prevValues.campaignAdTypeSettings,
            {
              tmpId: newTmpId,
              ...newSetting
            }
          ],
          property: {
            ...prevValues.property,
            addonProps: {
              ...prevValues.property.addonProps,
              campaignBidPrice: [
                ...prevValues.property.addonProps.campaignBidPrice,
                newBidPriceSetting
              ]
            }
          }
        };
      });
      onClose();
    },
    [adTypeId, channelGroupValues.campaignAdTypeSettings, setValues, onClose]
  );

  const validateName = useCallback(
    (name: string) => {
      const error = validateEmpty(name);
      if (error) {
        return error;
      }
      const duplicate = otherAdTypes.some((item) => {
        return item.name === name;
      });
      if (duplicate) {
        return i18n.t('channelGroupAdTypeForm.errors.duplicateName');
      }
    },
    [otherAdTypes]
  );

  const isDurationRangeOverlapping = useCallback(
    (
      setting1: { minDuration: number; maxDuration?: number },
      setting2: { minDuration: number; maxDuration?: number }
    ) => {
      const min1 = +setting1.minDuration;
      const max1 = setting1.maxDuration
        ? +setting1.maxDuration
        : MAX_DURATION_SECONDS;
      const min2 = +setting2.minDuration;
      const max2 = setting2.maxDuration
        ? +setting2.maxDuration
        : MAX_DURATION_SECONDS;
      return max1 >= min2 && max2 >= min1;
    },
    []
  );

  const validateDurationRange = useCallback(
    (
      minDuration: number,
      maxDuration?: number,
      screenDisplayType?: ScreenDisplayType
    ): string | undefined => {
      const overlapAdTypes = otherAdTypes.filter((item) => {
        return (
          item.state === CampaignAdTypeState.ACTIVE &&
          item.screenDisplayType === screenDisplayType &&
          isDurationRangeOverlapping(item.bannerTypes[0], {
            minDuration,
            maxDuration
          })
        );
      });
      if (overlapAdTypes.length > 0) {
        return i18n.t('channelGroupAdTypeForm.errors.durationOverlap', {
          adTypes: overlapAdTypes.map((item) => item.name).join(', ')
        });
      }
    },
    [otherAdTypes, isDurationRangeOverlapping]
  );

  const validateMinDuration = useCallback(
    (
      minDuration: number,
      maxDuration?: number,
      screenDisplayType?: ScreenDisplayType
    ) => {
      const error = validateMinimum(
        minDuration,
        isEpaperChannel ? EPAPER_MIN_DURATION_SECONDS : MIN_DURATION_SECONDS
      );
      if (error) {
        return error;
      }

      return validateDurationRange(minDuration, maxDuration, screenDisplayType);
    },
    [validateDurationRange, isEpaperChannel]
  );

  const validateMaxDuration = useCallback(
    (
      minDuration: number,
      maxDuration?: number,
      screenDisplayType?: ScreenDisplayType
    ) => {
      const error = validateMinMax(
        maxDuration,
        minDuration,
        MAX_DURATION_SECONDS
      );
      if (error) {
        return error;
      }

      return validateDurationRange(minDuration, maxDuration, screenDisplayType);
    },
    [validateDurationRange]
  );

  const validatePricingModel = useCallback((pricingModels: string[]) => {
    if (!pricingModels || pricingModels.length === 0) {
      return i18n.t('formValidate.labels.emptyError');
    }
    if (
      pricingModels.length === 1 &&
      pricingModels[0] === RtbCampaignPlanType.RS
    ) {
      return i18n.t(
        'channelGroupAdTypeForm.errors.cannotUseRsPricingModelAlone'
      );
    }
  }, []);

  const validateForm = useCallback(
    (values: CampaignAdTypeSettingFormData) => {
      let bannerTypeError;
      if (isStoreOutdoor) {
        const bannerType = values.bannerTypes[0];
        if (!bannerType) {
          bannerTypeError = {
            minDuration: i18n.t('formValidate.labels.emptyError'),
            width: i18n.t('formValidate.labels.emptyError'),
            height: i18n.t('formValidate.labels.emptyError'),
            supportedOutdoorTypes: i18n.t('formValidate.labels.emptyError')
          };
        } else {
          bannerTypeError = omitBy(
            {
              minDuration: validateMinDuration(
                bannerType.minDuration,
                bannerType.maxDuration,
                values.screenDisplayType
              ),
              // 電子紙的最長秒數必填（見初值那段的理由）；其他渠道沿用「留空等於不設
              // 上限」，有填才驗。
              maxDuration:
                isEpaperChannel || bannerType.maxDuration
                  ? validateMaxDuration(
                      bannerType.minDuration,
                      bannerType.maxDuration,
                      values.screenDisplayType
                    )
                  : undefined,
              width: validateEmpty(bannerType.width),
              height: validateEmpty(bannerType.height),
              supportedOutdoorTypes: validateEmpty(
                bannerType.supportedOutdoorTypes
              )
            },
            isUndefined
          );
        }
      }
      const nameI18nError = omitBy(
        {
          ja: validateEmpty(values.nameI18n?.ja),
          en: validateEmpty(values.nameI18n?.en),
          'zh-TW': validateEmpty(values.nameI18n?.['zh-TW'])
        },
        isUndefined
      );
      return omitBy(
        {
          name: validateName(values.name),
          screenDisplayType:
            channelGroupValues.channelType === ChannelType.BEACON
              ? undefined
              : validateEmpty(values.screenDisplayType),
          nameI18n: isEmpty(nameI18nError) ? undefined : nameI18nError,
          pricingModel: validatePricingModel(values.pricingModel),
          bannerTypes: isEmpty(bannerTypeError)
            ? undefined
            : { 0: bannerTypeError }
        },
        isEmpty
      );
    },
    [
      isStoreOutdoor,
      isEpaperChannel,
      channelGroupValues.channelType,
      validateName,
      validateMinDuration,
      validateMaxDuration,
      validatePricingModel
    ]
  );

  const screenDisplayTypeValues = useMemo(() => {
    const displayTypeValues = [ScreenDisplayType.SINGLE];
    if (isScreenSpliced) {
      displayTypeValues.push(ScreenDisplayType.MULTI_SPLICED);
    }
    if (isMultiScreenSameVideo) {
      displayTypeValues.push(ScreenDisplayType.SYNCHRONIZED_PLAYBACK);
    }
    return displayTypeValues;
  }, [isScreenSpliced, isMultiScreenSameVideo]);

  const screenDisplayOptions = useMemo(() => {
    return createSelectOptions(
      screenDisplayTypeValues,
      'screenDisplayType.',
      (value) => {
        if (value === ScreenDisplayType.SINGLE) {
          // i18n key: screenDisplayType.{channelType}.single
          // MOVABLE_OUTDOOR → 移動式單台, OUTDOOR → 固定式單台
          // (RKB-2541 Notion Final Spec).
          return `${camelCase(channelGroupValues.channelType)}.single`;
        }
        return camelCase(String(value));
      }
    );
  }, [screenDisplayTypeValues, channelGroupValues.channelType]);

  return (
    <Formik
      initialValues={initChannelGroupAdTypeSetting}
      onSubmit={onSubmit}
      validate={validateForm}
    >
      {({ values, setValues, setFieldValue, submitForm }) => {
        const onScreenDisplayOptionChange = (
          screenDisplayType: ScreenDisplayType
        ) => {
          setValues((prevValues) => {
            return {
              ...prevValues,
              screenDisplayType,
              bannerTypes: [
                {
                  ...prevValues.bannerTypes[0],
                  supportedOutdoorTypes: [],
                  width:
                    screenDisplayType === ScreenDisplayType.MULTI_SPLICED
                      ? screenPixelWidth * screenSplicedHorizontalScreenCount
                      : screenPixelWidth,
                  height:
                    screenDisplayType === ScreenDisplayType.MULTI_SPLICED
                      ? screenPixelHeight * screenSplicedVerticalScreenCount
                      : screenPixelHeight
                }
              ]
            };
          });
        };
        const onStateChange = (event) => {
          setFieldValue(
            'state',
            event.target.checked
              ? CampaignAdTypeState.ACTIVE
              : CampaignAdTypeState.INACTIVE
          );
        };
        let overlapError;
        if (isStoreOutdoor && values.bannerTypes && values.bannerTypes[0]) {
          const { minDuration, maxDuration } = values.bannerTypes[0];
          overlapError = validateDurationRange(
            minDuration,
            maxDuration,
            values.screenDisplayType
          );
        }

        const getOutdoorTypeValues = () => {
          // 電子紙看板沒有喇叭也不跑影片，Final Spec 明講「只有圖片，其他類型移除」
          // (ECOPD-5617)。用獨立的 IMAGE 類型而不是沿用 IMAGE_AUDIO 靠顯示層改字：
          // 銷售渠道的「支援的戶外廣告類型」是設定資料本身，講不清楚就會卡住建立
          // 電子紙廣告活動類型這件事。
          if (channelGroupValues.channelType === ChannelType.E_PAPER) {
            return [OutdoorType.IMAGE];
          }
          const outdoorTypeValues = [
            OutdoorType.VIDEO,
            OutdoorType.IMAGE_AUDIO,
            OutdoorType.HTML5
          ];
          if (values.screenDisplayType === ScreenDisplayType.MULTI_SPLICED) {
            return outdoorTypeValues.filter(
              (value) => value !== OutdoorType.HTML5
            );
          }
          return outdoorTypeValues;
        };

        const outdoorTypeOptions = createSelectOptions(
          getOutdoorTypeValues(),
          'outdoorForm.labels.outdoorType'
        );

        return (
          <Modal
            fullScreen
            title={i18n.t('channelGroupAdTypeForm.titles.addAdType')}
            primaryButton={{
              title: i18n.t('common.buttons.submit'),
              callback: submitForm
            }}
            secondaryButton={{
              title: i18n.t('common.buttons.close'),
              callback: onClose
            }}
            dismiss={onClose}
            animation={false}
          >
            <div className={styles.formContent}>
              <FormikField.Input
                name='name'
                label={i18n.t('channelGroupAdTypeForm.labels.name')}
              />
              {channelGroupValues.channelType !== ChannelType.BEACON && (
                <FormikField.Select
                  name='screenDisplayType'
                  label={i18n.t(
                    'channelGroupAdTypeForm.labels.screenDisplayType'
                  )}
                  options={screenDisplayOptions}
                  simpleValue
                  disabled={!isScreenSpliced && !isMultiScreenSameVideo}
                  onChange={onScreenDisplayOptionChange}
                />
              )}
              <FormikField.Input
                name='nameI18n.ja'
                label={i18n.t('channelGroupAdTypeForm.labels.nameI18n.ja')}
              />
              <FormikField.Input
                name='nameI18n.en'
                label={i18n.t('channelGroupAdTypeForm.labels.nameI18n.en')}
              />
              <FormikField.Input
                name='nameI18n.zh-TW'
                label={i18n.t('channelGroupAdTypeForm.labels.nameI18n.zh-tw')}
              />
              {isStoreOutdoor && (
                <>
                  <FormikField.InputGroup
                    name='bannerTypes.0.minDuration'
                    type='number'
                    label={i18n.t('channelGroupAdTypeForm.labels.minDuration')}
                    min={
                      isEpaperChannel
                        ? EPAPER_MIN_DURATION_SECONDS
                        : MIN_DURATION_SECONDS
                    }
                    max={values.bannerTypes[0].maxDuration}
                    postfix={i18n.t('common.units.seconds')}
                  />
                  <FormikField.InputGroup
                    name='bannerTypes.0.maxDuration'
                    type='number'
                    label={i18n.t('channelGroupAdTypeForm.labels.maxDuration')}
                    min={values.bannerTypes[0].minDuration}
                    max={MAX_DURATION_SECONDS}
                    postfix={i18n.t('common.units.seconds')}
                  />
                  <FormikField.Label
                    name='bannerTypes.0.width'
                    label={i18n.t('channelGroupAdTypeForm.labels.width')}
                  />
                  <FormikField.Label
                    name='bannerTypes.0.height'
                    label={i18n.t('channelGroupAdTypeForm.labels.height')}
                  />
                  <FormikField.Select
                    isMulti
                    simpleValue
                    name='bannerTypes.0.supportedOutdoorTypes'
                    label={i18n.t('channelGroupAdTypeForm.labels.outdoorType')}
                    options={outdoorTypeOptions}
                  />
                </>
              )}
              <FormikField.Select
                isMulti
                simpleValue
                name='pricingModel'
                label={i18n.t('channelGroupAdTypeForm.labels.pricingModel')}
                options={pricingModelOptions}
              />
              {adTypeId && (
                <SwitchField
                  label={i18n.t('channelGroupAdTypeForm.labels.state')}
                  value={
                    values.state === CampaignAdTypeState.ACTIVE ? true : false
                  }
                  name={'stateSwitch'}
                  onChange={onStateChange}
                  disabled={overlapError !== undefined}
                  hint={overlapError}
                />
              )}
            </div>
          </Modal>
        );
      }}
    </Formik>
  );
};
